import { Box, Text } from '@factorialco/deprecated-design-system'
import { Button, Icon } from '@factorialco/factorial-one-react'
import { Ai, ChevronRight } from '@factorialco/factorial-one-react/icons/app'
import {
  AcceptDraftTasksDocument,
  MeetingDetailFragment,
  MeetingDetailFragmentDoc,
  MeetingTasksListDocument,
  RejectDraftTasksDocument,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface Props {
  meeting: MeetingDetailFragment
}

export function SuggestedTasks({ meeting }: Props) {
  const { employee } = useCurrentSession()
  const [acceptDraftTasks, { loading: acceptingTasks }] = useMutation(AcceptDraftTasksDocument, {
    extractorFn: (data) => data.oneOnOnesMutations.bulkCreateOneononesTask,
  })
  const [rejectDraftTasks, { loading: rejectingTasks }] = useMutation(RejectDraftTasksDocument, {
    extractorFn: (data) => data.oneOnOnesMutations.rejectTasksDraftOneononesTalkingPoint,
  })

  const talkingPoint = meeting.talkingPoints?.at(0)
  const tasksByAsignee = groupBy(talkingPoint?.tasksDrafts, 'assigneeId')

  const firstParticipant = meeting.oneOnOne?.firstParticipant
  const secondParticipant = meeting.oneOnOne?.secondParticipant

  const firstParticipantTasks = firstParticipant
    ? tasksByAsignee[firstParticipant.access.id] || []
    : []
  const secondParticipantTasks = secondParticipant
    ? tasksByAsignee[secondParticipant.access.id] || []
    : []

  return (
    <Box
      alignItems="flexStart"
      background="purple200"
      borderRadius={{ all: 'abs012' }}
      padding="s4"
    >
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        width="full"
        paddingX="s6"
        paddingTop="s4"
        paddingBottom="s8"
      >
        <Box inline height="s32" flexDirection="row" alignItems="center">
          <Icon icon={Ai} color="grey800" />
          <Text.Normal size="200" weight="semibold">
            {i18n.t('one_on_ones.meeting.draft_tasks.title')}
          </Text.Normal>
        </Box>
        <Box flexDirection="row" gap="s4">
          <Button
            variant="ghost"
            label={i18n.t('one_on_ones.meeting.draft_tasks.reject')}
            loading={rejectingTasks}
            disabled={acceptingTasks}
            onClick={async () => {
              const response = await rejectDraftTasks({
                variables: { talkingPointId: Number(talkingPoint?.id) },
              })

              return handleResult(response, () => null)
            }}
          />
          <Button
            variant="outline"
            label={i18n.t('one_on_ones.meeting.draft_tasks.accept')}
            loading={acceptingTasks}
            disabled={rejectingTasks}
            onClick={async () => {
              const response = await acceptDraftTasks({
                variables: {
                  meetingId: meeting.id,
                  participantId: Number(employee?.id),
                  tasks: (talkingPoint?.tasksDrafts || []).map((task) => ({
                    name: task.name,
                    assigneeId: task.assigneeId,
                    content: task.content,
                    dueOn: task.dueOn,
                  })),
                },
                refetchQueries: [MeetingTasksListDocument],
                update: (cache) => {
                  // When accepting tasks, we're calling the tasks repository,
                  // so we can't update the cache from the mutation, we need to do it manually.
                  const meetingData = cache.readFragment({
                    id: cache.identify(meeting),
                    fragment: MeetingDetailFragmentDoc,
                    fragmentName: 'MeetingDetail',
                  })

                  const existingTalkingPoint = meetingData?.talkingPoints?.at(0)

                  if (meetingData && existingTalkingPoint) {
                    cache.writeFragment({
                      id: cache.identify(meeting),
                      fragment: MeetingDetailFragmentDoc,
                      fragmentName: 'MeetingDetail',
                      data: {
                        ...meetingData,
                        id: meeting.id,
                        talkingPoints: [{ ...existingTalkingPoint, tasksDrafts: [] }],
                      },
                    })
                  }
                },
              })

              return handleResult(response, () => null)
            }}
          />
        </Box>
      </Box>
      <Box
        background="grey000White"
        borderRadius={{ all: 'abs010' }}
        gap="s16"
        padding="s16"
        width="full"
      >
        {firstParticipant && firstParticipantTasks.length > 0 && (
          <Box>
            <Text.Normal weight="semibold">{firstParticipant.fullName}</Text.Normal>
            {firstParticipantTasks.map((task) => (
              <Box
                key={task.name}
                flexDirection="row"
                alignItems="center"
                gap="s4"
                paddingLeft="s4"
              >
                <Icon icon={ChevronRight} size="sm" />
                <Text.Normal>{task.name}</Text.Normal>
              </Box>
            ))}
          </Box>
        )}

        {secondParticipant && secondParticipantTasks.length > 0 && (
          <Box>
            <Text.Normal weight="semibold">{secondParticipant.fullName}</Text.Normal>
            {secondParticipantTasks.map((task) => (
              <Box
                key={task.name}
                flexDirection="row"
                alignItems="center"
                gap="s4"
                paddingLeft="s4"
              >
                <Icon icon={ChevronRight} size="sm" />
                <Text.Normal>{task.name}</Text.Normal>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}
